import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@components/atoms/ui-kit/button.tsx';
import PageHeader from '@components/layout/page-header';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import CreatePricePlanBoForm from '@components/back-office/price-plans/create-price-plan-bo-form';
import UpdatePricePlanBo from '@components/back-office/price-plans/update-price-plan-bo';

import {
  PricePlan,
  useGetPricePlansBo
} from '@utils/api/back-office/price-plans-bo/use-get-price-plans-bo';
import { useGetPricePlanBo } from '@utils/api/back-office/price-plans-bo/use-get-price-plan-bo';
import {
  PricePlanBo,
  PricePlanTier
} from '@utils/types/back-office/price-plans-bo';
import { formatDate } from '@utils/date';
import { Plus, Eye } from 'lucide-react';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';

export default function PricePlansBo() {
  const { t } = useTranslation();
  const { data: pricePlans, isLoading, isFetching } = useGetPricePlansBo();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPricePlan, setSelectedPricePlan] = useState<
    number | undefined
  >();

  // Create form state
  const [name, setName] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('0');
  const [tiers, setTiers] = useState<PricePlanTier[]>([
    { start: 0, end: null, price_per_unit: '0' }
  ]);

  // Edit form state
  const [newName, setNewName] = useState('');
  const [newPricePerUnit, setNewPricePerUnit] = useState('');
  const [newTiers, setNewTiers] = useState<PricePlanTier[]>([
    { start: 0, end: null, price_per_unit: '' }
  ]);

  const { data: pricePlan, isLoading: pricePlanIsLoading } = useGetPricePlanBo(
    selectedPricePlan ?? 0
  );

  type Row = {
    id: number;
    name: string;
    last_modified: string;
  };

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'id',
        header: t('id'),
        field: 'id',
        accessorKey: 'id',
        maxWidth: 100,
        minWidth: 80,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number',
        cursor: 'pointer',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'name',
        header: t('name'),
        field: 'name',
        accessorKey: 'name',
        minWidth: 280,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cursor: 'pointer',
        enableRowCopyOnClick: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'last_modified',
        header: t('last-modified'),
        field: 'last_modified',
        accessorKey: 'last_modified',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'date',
        cursor: 'pointer',
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  const rows = useMemo<Row[]>(() => {
    const list = pricePlans?.result ?? [];
    return list
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a.last_modified).getTime();
        const dateB = new Date(b.last_modified).getTime();
        // Handle invalid dates by treating them as oldest
        if (isNaN(dateB) && isNaN(dateA)) return 0;
        if (isNaN(dateB)) return -1;
        if (isNaN(dateA)) return 1;
        return dateB - dateA;
      })
      .map((pp: PricePlan) => ({
        id: pp.id,
        name: pp.name,
        last_modified: formatDate(pp.last_modified)
      }));
  }, [pricePlans]);

  const handleCellClick = (row: Row) => {
    setSelectedPricePlan(row.id);
    setIsEditModalOpen(true);
  };

  // Actions for mobile & desktop
  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'view',
        label: t('view'),
        icon: <Eye className="h-4 w-4" />,
        intent: 'primary',
        run: (row: Row) => {
          setSelectedPricePlan(row.id);
          setIsEditModalOpen(true);
        }
      }
    ],
    [t]
  );

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col px-4 py-2">
      {/* Create modal */}
      <CreatePricePlanBoForm
        setName={setName}
        setPricePerUnit={setPricePerUnit}
        setTiers={setTiers}
        pricePerUnit={pricePerUnit}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        tiers={tiers}
        name={name}
      />

      <PageHeader
        title={t('price-plans')}
        caption={t('view-and-update-price-plans-below')}
      >
        <Button
          icon={<Plus width={14} />}
          onClick={() => setIsModalOpen(true)}
          label={t('new-price-plan')}
        />
      </PageHeader>

      <div className="w-full flex-1 min-h-0 flex flex-col">
        <DataGridV2<Row>
          data={rows as PricePlanBo[] as unknown as Row[]}
          columns={columns}
          actions={actions}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering
          manualFiltering
          totalItems={pricePlans?.total_results ?? rows.length}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          fill
          dense
          grid={false}
          striped
          loading={isLoading || isFetching || !pricePlans}
          onCellClick={handleCellClick}
          getRowId={row => String(row.id)}
        />

        {/* Edit modal */}
        {isEditModalOpen && (
          <UpdatePricePlanBo
            pricePlan={pricePlan}
            setNewName={setNewName}
            setNewPricePerUnit={setNewPricePerUnit}
            setNewTiers={setNewTiers}
            pricePlanIsLoading={pricePlanIsLoading}
            isEditModalOpen={isEditModalOpen}
            setIsEditModalOpen={setIsEditModalOpen}
            newName={newName}
            newTiers={newTiers}
            newPricePerUnit={newPricePerUnit}
            selectedPricePlan={selectedPricePlan}
          />
        )}
      </div>
    </div>
  );
}
